import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { Activity, MicOff, Brain, Target, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'

const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`)

function Stat({ icon: Icon, label, value, sub, tone = 'slate' }) {
  const tones = {
    slate:   'text-slate-700 bg-slate-50 border-slate-200',
    amber:   'text-amber-700 bg-amber-50 border-amber-200',
    red:     'text-red-700 bg-red-50 border-red-200',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  }
  return (
    <div className={cn('rounded-lg border p-4', tones[tone])}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide opacity-80">
        <Icon className="h-3.5 w-3.5" />{label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-xs opacity-75">{sub}</div>}
    </div>
  )
}

function Chips({ items, className, empty }) {
  if (!items || items.length === 0) return <span className="text-xs text-slate-400">{empty}</span>
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((t, i) => (
        <span key={i} className={cn('rounded border px-1.5 py-0.5 text-xs font-medium', className)}>{t}</span>
      ))}
    </div>
  )
}

function Row({ r }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpen(!open)}>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1.5">
            <ChevronRight className={cn('h-3.5 w-3.5 text-slate-400 transition-transform', open && 'rotate-90')} />
            <span className="font-medium text-slate-800">{r.patient}</span>
          </div>
        </td>
        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.visit_date}</td>
        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.n_truth}</td>
        <td className="px-3 py-2 text-right tabular-nums text-slate-700">
          {r.n_ai === 0 ? <span className="text-red-600 font-medium">0</span> : r.n_ai}
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-900">{r.n_matched}</td>
        <td className="px-3 py-2 text-right tabular-nums">{pct(r.recall)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{pct(r.precision)}</td>
      </tr>
      {open && (
        <tr className="bg-slate-50/80">
          <td colSpan={7} className="px-3 py-3">
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <div className="mb-1 text-xs font-semibold text-emerald-700">Matched — both agree</div>
                <Chips items={r.matched} className="bg-emerald-50 text-emerald-700 border-emerald-200" empty="nothing matched" />
              </div>
              <div>
                <div className="mb-1 text-xs font-semibold text-amber-700">Missed — she charted it, the scribe didn't</div>
                <Chips items={r.missed} className="bg-amber-50 text-amber-700 border-amber-200" empty="nothing missed" />
              </div>
              <div>
                <div className="mb-1 text-xs font-semibold text-red-700">Invented — not on her chart</div>
                <Chips items={r.extra} className="bg-red-50 text-red-700 border-red-200" empty="nothing invented" />
              </div>
            </div>
            {r.excerpt && (
              <div className="mt-3 rounded border border-slate-200 bg-white p-2">
                <div className="text-xs font-semibold text-slate-500">Transcript opening</div>
                <div className="mt-0.5 text-xs italic text-slate-600">"{r.excerpt}…"</div>
              </div>
            )}
            <div className="mt-2 text-xs text-slate-400">
              model: {r.model} · her chart spans {r.truth_visits} visit{r.truth_visits === 1 ? '' : 's'} · right teeth: {pct(r.tooth_recall)}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export default function ChartShadow({ board, rows, meta }) {
  const o = board.overall || {}
  const h = board.heard || {}
  const u = board.unheard || {}

  return (
    <DashboardLayout title="Chart shadow">
      <div className="mx-auto max-w-7xl space-y-5 p-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
            <Activity className="h-5 w-5 text-slate-500" />Chart shadow — can the AI chart for us yet?
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            For every visit with both a chair-side recording and the chart Dr Chalita physically wrote, the scribe
            drafts what it thinks happened and is scored against her sheet. <strong>Nothing here is used clinically</strong> —
            she keeps charting on paper. This is the instrument that says when switching is worth considering.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Target} label="Precision" value={pct(o.precision)} tone={o.precision >= 0.9 ? 'emerald' : 'red'}
                sub={`of ${o.claimed || 0} claims, ${o.matched || 0} are on her chart`} />
          <Stat icon={Brain} label="Recall" value={pct(o.recall)} tone={o.recall >= 0.9 ? 'emerald' : 'red'}
                sub={`${o.missed || 0} of ${o.findings || 0} charted findings missed`} />
          <Stat icon={MicOff} label="Heard nothing" value={u.pairs || 0} tone="amber"
                sub={`${u.findings || 0} charted findings with no clinical speech captured`} />
          <Stat icon={CheckCircle2} label="Visits graded" value={o.pairs || 0} tone="slate"
                sub={`${meta.all_rows} pairs · last run ${meta.last_run || '—'}`} />
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <strong>Two different failures, two different fixes.</strong> On the {h.pairs || 0} visits where the scribe
              heard clinical speech it scores <strong>{pct(h.recall)} recall / {pct(h.precision)} precision</strong>. On
              the other {u.pairs || 0} it produced <strong>nothing at all</strong> — those are recordings of room
              conversation, a microphone and capture problem, not a comprehension one. Recall is also understated
              wherever her sheet is a multi-visit case plan rather than one visit's work.
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-sm font-semibold text-slate-800">Most missed — the build backlog</div>
            <div className="mt-2 space-y-1">
              {(board.misses || []).map(([fam, n]) => (
                <div key={fam} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{fam}</span>
                  <span className="tabular-nums text-slate-500">{n}×</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-sm font-semibold text-slate-800">Most invented — treatment not on her chart</div>
            <div className="mt-2 space-y-1">
              {(board.invented || []).map(([fam, n]) => (
                <div key={fam} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{fam}</span>
                  <span className="tabular-nums text-slate-500">{n}×</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Patient</th>
                <th className="px-3 py-2 text-left font-medium">Visit</th>
                <th className="px-3 py-2 text-right font-medium">She charted</th>
                <th className="px-3 py-2 text-right font-medium">Scribe said</th>
                <th className="px-3 py-2 text-right font-medium">Agreed</th>
                <th className="px-3 py-2 text-right font-medium">Recall</th>
                <th className="px-3 py-2 text-right font-medium">Precision</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => <Row key={r.id} r={r} />)}
            </tbody>
          </table>
        </div>

        {meta.total_pages > 1 && (
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>Page {meta.page} of {meta.total_pages} · {meta.total} graded visits</span>
            <div className="flex gap-2">
              <button disabled={meta.page <= 1} onClick={() => router.get('/chart-shadow', { page: meta.page - 1 })}
                      className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40">Previous</button>
              <button disabled={meta.page >= meta.total_pages} onClick={() => router.get('/chart-shadow', { page: meta.page + 1 })}
                      className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
